'use client';

/**
 * "Top earners this week" — the compact Top Earners board for the site
 * (docs/LEADERBOARD_MARQ_EARNED_PROMPT.md §4 "The site"): the top 5 plus you.
 *
 * Reads the public GET /api/leaderboard/earned (backend lib/earned.ts). Ranked
 * by KHARMA the game PAID the player — mission rewards, talent bonuses, versus net
 * winnings, season prizes; spending never counts. With `game` it shows that
 * game's board when the game pays KHARMA, otherwise the cross-game board.
 * Nicknames only. One fetch on mount; nothing polls.
 */

import { useEffect, useState } from 'react';
import MarqCoin from './MarqCoin';

const API = process.env.NEXT_PUBLIC_API_BASE ?? '';

/** `avatar` is the account's picture (`/api/avatar/<key>`), null when it has none. */
interface EarnRow { rank: number; nickname: string; chip: string | null; total: number; type: string; me?: boolean; avatar?: string | null }
interface EarnBoard {
  game: string;
  rows: EarnRow[];
  total: number;
  period: { label: string; endsAt: string | null };
  me: { rank: number | null; total: number; percentile: number | null; next: { nickname: string; gap: number } | null } | null;
}

const MEDAL = ['bg-amber-400 text-amber-950', 'bg-slate-300 text-slate-900', 'bg-orange-400 text-orange-950'];
const AV = ['#ff4b2b', '#ff8a3a', '#ffc53d', '#a4ff5c', '#4fe6ff', '#9d8cff', '#ff6fae', '#5affc8'];
const avColor = (s: string) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return AV[Math.abs(h) % AV.length]; };
const fmt = (n: number) => Math.floor(n).toLocaleString('en-US');

/** The account's picture, or the coloured initial when it has none or it fails to load. */
function RowFace({ nickname, avatar }: { nickname: string; avatar?: string | null }) {
  const [bad, setBad] = useState(false);
  useEffect(() => setBad(false), [avatar]);
  if (avatar && !bad) {
    // eslint-disable-next-line @next/next/no-img-element -- a small, already-sized avatar
    return <img src={API + avatar} className="h-6 w-6 shrink-0 rounded-full object-cover" alt="" onError={() => setBad(true)} />;
  }
  return (
    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-black text-ink-950" style={{ background: avColor(nickname) }} aria-hidden>
      {nickname.charAt(0).toUpperCase()}
    </span>
  );
}

function left(endsAt: string | null): string | null {
  if (!endsAt) return null;
  const ms = Date.parse(endsAt) - Date.now();
  if (!(ms > 0)) return null;
  const d = Math.floor(ms / 864e5), h = Math.floor((ms % 864e5) / 36e5);
  return d ? `${d}d ${h}h` : `${h}h`;
}

export default function TopEarnersWidget({ game, className = '' }: { game?: string; className?: string }) {
  const [data, setData] = useState<EarnBoard | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(`${API}/api/leaderboard/earned?period=week&limit=5${game ? `&game=${encodeURIComponent(game)}` : ''}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j: EarnBoard) => { if (live) setData(j); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [game]);

  if (failed) return null;
  const ends = data ? left(data.period.endsAt) : null;
  const me = data?.me ?? null;
  const meOnBoard = !!data?.rows.some((r) => r.me);

  return (
    <section
      aria-label="Top earners this week"
      className={'shrink-0 overflow-hidden rounded-xl border border-ink-200 bg-white dark:border-ink-700 dark:bg-ink-900 ' + className}
    >
      <div className="flex items-center gap-2 border-b border-ink-100 bg-gradient-to-r from-amber-50 to-transparent px-3 py-2 dark:border-ink-800 dark:from-amber-500/10">
        <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-amber-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M7 3.5h10V9a5 5 0 0 1-10 0V3.5zM7 5.5H3.5A3.5 3.5 0 0 0 7 10M17 5.5h3.5A3.5 3.5 0 0 1 17 10M12 14v4M8 21h8M9.5 18h5" />
        </svg>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-700 dark:text-ink-200">
          Top earners <span className="text-ink-400">this week</span>
        </p>
        <span className="ml-auto text-[10.5px] tabular-nums text-ink-400">
          {data?.game === '*' ? 'all games · ' : ''}{ends ? `resets in ${ends}` : ''}
        </span>
      </div>

      {!data ? (
        <ul className="space-y-1.5 px-3 py-2.5" aria-hidden>
          {[0, 1, 2].map((i) => <li key={i} className="h-6 animate-pulse rounded bg-ink-100 dark:bg-ink-800" />)}
        </ul>
      ) : !data.rows.length ? (
        <p className="px-3 py-3 text-[12.5px] text-ink-500 dark:text-ink-400">No earnings yet this week. Be the first: every KHARMA a mission pays you counts.</p>
      ) : (
        <ol className="divide-y divide-ink-100 dark:divide-ink-800">
          {data.rows.map((r) => (
            <li key={r.rank} className={'flex items-center gap-2.5 px-3 py-1.5 ' + (r.me ? 'bg-cyan-50 dark:bg-cyan-500/10' : '')}>
              <span className={'grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10.5px] font-black tabular-nums ' + (MEDAL[r.rank - 1] ?? 'text-ink-400')}>{r.rank}</span>
              <RowFace nickname={r.nickname} avatar={r.avatar} />
              <span className={'min-w-0 flex-1 truncate text-[13px] font-semibold ' + (r.me ? 'text-cyan-700 dark:text-cyan-300' : 'text-ink-800 dark:text-ink-100')}>
                {r.nickname}{r.me && <span className="ml-1 text-[10.5px] font-bold uppercase text-cyan-600 dark:text-cyan-400">you</span>}
              </span>
              <span className="hidden shrink-0 text-[10px] font-semibold uppercase tracking-wider text-ink-400 sm:inline">{r.type}</span>
              <span className="inline-flex shrink-0 items-center gap-1 text-[13px] font-black tabular-nums text-amber-600 dark:text-amber-300">
                <MarqCoin className="h-3.5 w-3.5" />{fmt(r.total)}
              </span>
            </li>
          ))}
        </ol>
      )}

      {data && (
        <div className="border-t border-ink-100 px-3 py-2 dark:border-ink-800">
          {!me ? (
            <p className="text-[12px] text-ink-500 dark:text-ink-400"><span className="font-semibold text-ink-700 dark:text-ink-200">You:</span> sign in to rank.</p>
          ) : me.rank ? (
            !meOnBoard && (
              <p className="flex items-center gap-2 text-[12px] text-ink-600 dark:text-ink-300">
                <span className="font-bold text-cyan-700 dark:text-cyan-300">You · #{fmt(me.rank)}</span>
                <span className="text-ink-400">Top {me.percentile}%</span>
                <span className="ml-auto inline-flex items-center gap-1 font-black tabular-nums text-amber-600 dark:text-amber-300"><MarqCoin className="h-3.5 w-3.5" />{fmt(me.total)}</span>
              </p>
            )
          ) : (
            <p className="text-[12px] text-ink-500 dark:text-ink-400"><span className="font-semibold text-ink-700 dark:text-ink-200">You:</span> not ranked yet this week. Clear a mission to get on the board.</p>
          )}
          {me?.next && me.rank && (
            <p className="mt-0.5 text-[11.5px] text-ink-400"><MarqCoin className="mr-1 h-3 w-3 align-[-0.15em]" />{fmt(me.next.gap)} KHARMA to pass <span className="font-semibold text-ink-600 dark:text-ink-300">{me.next.nickname}</span></p>
          )}
          <p className="mt-1 text-[10.5px] text-ink-400">Ranked by KHARMA earned from the game. Spending never counts.</p>
        </div>
      )}
    </section>
  );
}
